/**
 * A2A (Agent2Agent) domain event schemas.
 *
 * Typed payload validation for A2A protocol gateway Mycelium events.
 * These events bridge external A2A tasks (from agents built on Google ADK,
 * a2a-python, a2a-js, etc.) to the internal Mycelium event backbone.
 *
 * Lifecycle mapping (A2A TaskState → Mycelium event type):
 *   SUBMITTED      → a2a.task.requested   (inbound: external agent sent a task)
 *   WORKING        → a2a.task.status_update (lifecycle transition)
 *   INPUT_REQUIRED → a2a.task.input_required (HITL: awaiting human approval)
 *   COMPLETED      → a2a.task.completed    (terminal: success)
 *   FAILED         → a2a.task.failed       (terminal: error)
 *   CANCELED       → a2a.task.canceled     (terminal: client canceled)
 *
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module a2a.events
 * @since 2026-07-03
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Shared sub-schemas ──────────────────────────────────────────────────

/**
 * A2A TaskState enum — mirrors the A2A v1.0 protocol specification.
 * @see https://a2a-protocol.org/latest/specification/
 */
export const A2aTaskStateSchema = z.enum([
  'submitted',
  'working',
  'input_required',
  'auth_required',
  'completed',
  'failed',
  'canceled',
  'rejected',
])
export type A2aTaskState = z.infer<typeof A2aTaskStateSchema>

/**
 * A minimal A2A Message representation for event payloads.
 * Full A2A Message objects are stored in a2a_tasks.message_history (JSONB);
 * events carry only the fields needed for routing and audit.
 */
export const A2aMessageRefSchema = z.object({
  role: z.enum(['user', 'agent']),
  /** Concatenated text parts for quick display; full parts in a2a_tasks. */
  text: z.string().optional(),
  messageId: z.string().optional(),
})
export type A2aMessageRef = z.infer<typeof A2aMessageRefSchema>

/**
 * Reference to the marketplace agent instance that owns this task.
 * Links the A2A task to a FlowState principal + template.
 */
export const A2aAgentContextSchema = z.object({
  /** marketplace_agent_instances.id */
  agentInstanceId: z.string(),
  /** mycelium_principals.id of the external agent */
  principalId: z.string(),
  /** agent_templates.id (for system prompt + tool constraints) */
  templateId: z.string().optional(),
  /** agent_templates.slug (human-readable, for routing/logging) */
  templateSlug: z.string().optional(),
  /** Organization scope */
  organizationId: z.string().optional(),
})
export type A2aAgentContext = z.infer<typeof A2aAgentContextSchema>

// ── Event schemas ───────────────────────────────────────────────────────

/**
 * Inbound: An external A2A agent sent a message/task to the FlowState gateway.
 * The gateway publishes this event to enqueue work into Mycelium → brain.
 */
export const A2aTaskRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('a2a'),
  type: z.literal('a2a.task.requested'),
  payload: z.object({
    /** A2A server-generated task ID (UUID). */
    taskId: z.string(),
    /** A2A contextId — groups related tasks in a conversation. */
    contextId: z.string().optional(),
    /** The inbound user message that initiated/continued the task. */
    message: A2aMessageRefSchema,
    /** Agent identity + marketplace linkage. */
    agentContext: A2aAgentContextSchema,
    /** Cortex run ID once the brain accepts the task (set by worker). */
    runId: z.string().optional(),
    /** A2A protocol version the caller used (e.g. "1.0.0", "0.3.0"). */
    protocolVersion: z.string().optional(),
    requestedAt: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Lifecycle: A2A task state transition (e.g. SUBMITTED → WORKING).
 * Published by the worker/brain as the task progresses.
 */
export const A2aTaskStatusUpdateEvent = BackboneEventSchema.extend({
  domain: z.literal('a2a'),
  type: z.literal('a2a.task.status_update'),
  payload: z.object({
    taskId: z.string(),
    contextId: z.string().optional(),
    previousState: A2aTaskStateSchema.optional(),
    newState: A2aTaskStateSchema,
    agentContext: A2aAgentContextSchema.optional(),
    runId: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Terminal: A2A task completed successfully.
 * Artifacts are stored in a2a_tasks.artifacts (JSONB); the event carries a summary.
 */
export const A2aTaskCompletedEvent = BackboneEventSchema.extend({
  domain: z.literal('a2a'),
  type: z.literal('a2a.task.completed'),
  payload: z.object({
    taskId: z.string(),
    contextId: z.string().optional(),
    agentContext: A2aAgentContextSchema.optional(),
    runId: z.string().optional(),
    /** Count of artifacts produced (full artifacts in a2a_tasks). */
    artifactCount: z.number().optional(),
    /** Final assistant message text (truncated for event payload). */
    finalText: z.string().optional(),
    completedAt: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Terminal: A2A task failed.
 */
export const A2aTaskFailedEvent = BackboneEventSchema.extend({
  domain: z.literal('a2a'),
  type: z.literal('a2a.task.failed'),
  payload: z.object({
    taskId: z.string(),
    contextId: z.string().optional(),
    agentContext: A2aAgentContextSchema.optional(),
    runId: z.string().optional(),
    errorMessage: z.string(),
    /** Whether the failure is retryable (worker decides DLQ routing). */
    retryable: z.boolean().optional(),
    failedAt: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Interrupted: A2A task requires human input (HITL checkpoint).
 * Maps to cortex_runs.status = 'awaiting_input' and surfaces in ApprovalInbox.
 */
export const A2aTaskInputRequiredEvent = BackboneEventSchema.extend({
  domain: z.literal('a2a'),
  type: z.literal('a2a.task.input_required'),
  payload: z.object({
    taskId: z.string(),
    contextId: z.string().optional(),
    agentContext: A2aAgentContextSchema.optional(),
    runId: z.string().optional(),
    /** The question/prompt the agent is asking the human. */
    prompt: z.string().optional(),
    /** Proposed action awaiting approval (JSON-serialized). */
    proposedAction: z.record(z.unknown()).optional(),
    requestedAt: z.string().optional(),
    source: z.string().optional(),
  }),
})

/**
 * Terminal: A2A task was canceled by the client.
 */
export const A2aTaskCanceledEvent = BackboneEventSchema.extend({
  domain: z.literal('a2a'),
  type: z.literal('a2a.task.canceled'),
  payload: z.object({
    taskId: z.string(),
    contextId: z.string().optional(),
    agentContext: A2aAgentContextSchema.optional(),
    runId: z.string().optional(),
    reason: z.string().optional(),
    canceledAt: z.string().optional(),
    source: z.string().optional(),
  }),
})

// ── All A2A events ──────────────────────────────────────────────────────

export const A2aDomainEvents = [
  A2aTaskRequestedEvent,
  A2aTaskStatusUpdateEvent,
  A2aTaskCompletedEvent,
  A2aTaskFailedEvent,
  A2aTaskInputRequiredEvent,
  A2aTaskCanceledEvent,
] as const

// ── Inferred types ──────────────────────────────────────────────────────

export type A2aTaskRequestedEvent = z.infer<typeof A2aTaskRequestedEvent>
export type A2aTaskStatusUpdateEvent = z.infer<typeof A2aTaskStatusUpdateEvent>
export type A2aTaskCompletedEvent = z.infer<typeof A2aTaskCompletedEvent>
export type A2aTaskFailedEvent = z.infer<typeof A2aTaskFailedEvent>
export type A2aTaskInputRequiredEvent = z.infer<typeof A2aTaskInputRequiredEvent>
export type A2aTaskCanceledEvent = z.infer<typeof A2aTaskCanceledEvent>
